import { useState, useEffect } from "react";
import apiService from "../services/apiService";
import { handleUpdateError, handleCreateError, handleDeleteError } from "../utils/errorHandler";
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  TableContainer,
  TableCell,
  TableBody,
  TableHead,
  Paper,
  Table,
  TableRow,
  Typography,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as EditIcon } from "../Icons/Edit.svg";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";
import { ThemeProvider } from "@mui/material/styles";
import Table_theme from "../Themes/TableTheme";
import theme from "../Themes/FormThemes";

const SubstageForm = ({ open, handleClose, selectedBJob }) => {
  const [formData, setFormData] = useState({
    cl_id: "",
    client_name: "",
    env_id: "",
    environment_name: "",
    app_id: "",
    application_name: "",
    job_id: "",
    jobs_script: "",
    substage_id: "",
    substage_desc: "",
    comments: "",
  });

  const [errors, setErrors] = useState({});
  const [dbSubstages, setDbSubstages] = useState([]);
  const [isEditingDbSubstage, setIsEditingDbSubstage] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [originalFormData, setOriginalFormData] = useState({});
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [loading, setLoading] = useState(false);

  const columnLabels = {
    client_name: "Client",
    environment_name: "Environment",
    application_name: "Application",
    jobs_script: "Job",
    substage_id: "Substage ID",
    substage_desc: "Substage Description",
    comments: "Comments",
  };

  const columns = Object.keys(columnLabels);

  useEffect(() => {
    if (open && selectedBJob) {
      setFormData((prev) => ({
        ...prev,
        cl_id: selectedBJob.cl_id || "",
        client_name: selectedBJob.client_name || "",
        env_id: selectedBJob.env_id || "",
        environment_name: selectedBJob.environment_name || "",
        app_id: selectedBJob.app_id || "",
        application_name: selectedBJob.application_name || "",
        job_id: selectedBJob.job_id || "",
        jobs_script: selectedBJob.jobs_script || "",
      }));
      fetchDbSubstages(selectedBJob.job_id);
    }
  }, [open, selectedBJob]);

  // Track form changes for edit mode
  useEffect(() => {
    if (isEditingDbSubstage && Object.keys(originalFormData).length > 0) {
      const hasChanged =
        JSON.stringify(formData) !== JSON.stringify(originalFormData);
      setIsFormChanged(hasChanged);
    }
  }, [formData, originalFormData, isEditingDbSubstage]);

  const fetchDbSubstages = async (job_id) => {
    setLoading(true);
    try {
      const data = await apiService.get(`/substage/${job_id}`);
      setDbSubstages(Array.isArray(data) ? data : []);
    } catch (error) {
      if (error.message && error.message.includes("404")) {
        setDbSubstages([]);
        return;
      }
      console.error("Failed to fetch DB substages", error);
      setDbSubstages([]);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (data) => {
    const newErrors = {};

    // Substage Description validation
    if (!data.substage_desc || !data.substage_desc.trim()) {
      newErrors.substage_desc = "Substage Description is required";
    } else {
      const value = data.substage_desc.trim();

      // Check if it contains only special characters
      if (/^[^a-zA-Z0-9]+$/.test(value)) {
        newErrors.substage_desc =
          "Substage Description cannot contain only special characters";
      }
      // Check if it contains only numbers
      else if (/^\d+$/.test(value)) {
        newErrors.substage_desc =
          "Substage Description cannot contain only numbers";
      }
      // Check if it contains at least one letter
      else if (!/[a-zA-Z]/.test(value)) {
        newErrors.substage_desc =
          "Substage Description must contain at least one letter";
      }
    }

    // Validate comments field if provided
    if (data.comments && data.comments.trim()) {
      const value = data.comments.trim();

      // Check if it contains only special characters
      if (/^[^a-zA-Z0-9]+$/.test(value)) {
        newErrors.comments = "Comments cannot contain only special characters";
      }
      // Check if it contains only numbers
      else if (/^\d+$/.test(value)) {
        newErrors.comments = "Comments cannot contain only numbers";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const resetForm = () => {
    setFormData({
      cl_id: selectedBJob.cl_id || "",
      client_name: selectedBJob.client_name || "",
      env_id: selectedBJob.env_id || "",
      environment_name: selectedBJob.environment_name || "",
      app_id: selectedBJob.app_id || "",
      application_name: selectedBJob.application_name || "",
      job_id: selectedBJob.job_id || "",
      jobs_script: selectedBJob.jobs_script || "",
      substage_id: "",
      substage_desc: "",
      comments: "",
    });
    setErrors({});
    setOriginalFormData({});
    setIsFormChanged(false);
    setIsEditingDbSubstage(false);
    handleClose();
  };

  const handleSubmit = async () => {
    // Clear previous errors before validation
    setErrors({});
    
    if (!validateForm(formData)) return;

    try {
      if (isEditingDbSubstage) {
        // Update existing substage
        await apiService.put(`/substage/${formData.substage_id}`, formData);
        setSuccessMessage("Substage updated successfully!");
      } else {
        // Add new substage
        await apiService.post("/substage", [formData]);
        setSuccessMessage("Substage added successfully!");
      }

      setTimeout(() => setSuccessMessage(""), 3000);
      resetForm();
      fetchDbSubstages(selectedBJob.job_id);
    } catch (error) {
      console.error("Error saving substage:", error);
      const errorMessage = isEditingDbSubstage
        ? handleUpdateError(error, "Substage")
        : handleCreateError(error, "Substage");
      setSuccessMessage(errorMessage);
      setTimeout(() => setSuccessMessage(""), 5000);
    }
  };

  const handleDbEdit = (row) => {
    const editFormData = {
      cl_id: row.cl_id || "",
      client_name: row.client_name || "",
      env_id: row.env_id || "",
      environment_name: row.environment_name || "",
      app_id: row.app_id || "",
      application_name: row.application_name || "",
      job_id: row.job_id || "",
      jobs_script: row.jobs_script || "",
      substage_id: row.substage_id || "",
      substage_desc: row.substage_desc || "",
      comments: row.comments || "",
    };

    setFormData(editFormData);
    setOriginalFormData({ ...editFormData });
    setIsEditingDbSubstage(true);
    setIsFormChanged(false);
  };

  const combinedSubstages = [...dbSubstages];

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick") return;
        handleClose();
      }}
      maxWidth="xl"
      fullWidth
    >
      <DialogTitle>
        {isEditingDbSubstage
          ? `Edit Substage – ${formData.substage_desc || ""}`
          : `Substage Job – ${selectedBJob?.jobs_script || selectedBJob?.job_id || ""}`}
        <IconButton
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {successMessage && (
          <Alert
            severity={
              successMessage.includes("successfully") ||
              successMessage.includes("Activated") ||
              successMessage.includes("Deactivated")
                ? "success"
                : "error"
            }
            sx={{ mb: 2 }}
          >
            {successMessage}
          </Alert>
        )}

        <ThemeProvider theme={theme}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(6, 1fr)",
              gap: 1,
              mt: 1,
            }}
          >
            <TextField
              fullWidth
              size="small"
              label="Client"
              value={formData.client_name}
              disabled
              sx={{
                "& .MuiInputBase-root": {
                  height: 35,
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.75rem",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.75rem",
                },
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="Environment"
              value={formData.environment_name}
              disabled
              sx={{
                "& .MuiInputBase-root": {
                  height: 35,
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.75rem",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.75rem",
                },
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="Application"
              value={formData.application_name}
              disabled
              sx={{
                "& .MuiInputBase-root": {
                  height: 35,
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.75rem",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.75rem",
                },
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="Job"
              value={formData.jobs_script}
              disabled
              sx={{
                "& .MuiInputBase-root": {
                  height: 35,
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.75rem",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.75rem",
                },
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="Substage Description"
              name="substage_desc"
              value={formData.substage_desc}
              onChange={handleChange}
              required
              error={Boolean(errors.substage_desc)}
              helperText={errors.substage_desc || ""}
              sx={{
                "& .MuiInputBase-root": {
                  height: 35,
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.75rem",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.75rem",
                },
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="Comments"
              name="comments"
              value={formData.comments}
              onChange={handleChange}
              error={Boolean(errors.comments)}
              helperText={errors.comments || ""}
              sx={{
                "& .MuiInputBase-root": {
                  height: 35,
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.75rem",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.75rem",
                },
              }}
            />
          </Box>
        </ThemeProvider>

        <DialogActions>
          <Button onClick={resetForm}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isEditingDbSubstage && !isFormChanged}
          >
            {isEditingDbSubstage ? "Update" : "Add"}
          </Button>
        </DialogActions>

        <ThemeProvider theme={Table_theme}>
        {combinedSubstages.length > 0 && (
          <Box sx={{ mt: 2 }}>
              <Paper elevation={3}>
                <TableContainer sx={{ maxHeight: "50vh" }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Actions</TableCell>
                        {columns.map((col) => (
                          <TableCell key={col}>{columnLabels[col]}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell
                            colSpan={columns.length + 1}
                            sx={{
                              position: "sticky",
                              left: 0,
                              textAlign: "left",
                              padding: "20px",
                              backgroundColor: "white",
                              zIndex: 1,
                            }}
                          >
                            Loading...
                          </TableCell>
                        </TableRow>
                      ) : combinedSubstages.length > 0 ? (
                        combinedSubstages.map((row, idx) => (
                          <TableRow key={idx}>
                            <TableCell>
                              <Box display="flex" gap={1} flexWrap="nowrap">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDbEdit(row)}
                                  sx={{ color: "primary.main" }}
                                  title="Edit"
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setDeleteRow(row);
                                    setOpenDeleteDialog(true);
                                  }}
                                  sx={{ color: "error.main" }}
                                  title="Delete"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </TableCell>
                            {columns.map((col) => (
                              <TableCell key={col}>
                                {col === 'client_name' && (row.client_name || row.cl_id || "NA")}
                                {col === 'environment_name' && (row.environment_name || row.env_id || "NA")}
                                {col === 'application_name' && (row.application_name || row.app_id || "NA")}
                                {col === 'jobs_script' && (row.jobs_script || row.job_id || "NA")}
                                {col === 'substage_id' && (row.substage_id || "NA")}
                                {col === 'substage_desc' && (row.substage_desc || "NA")}
                                {col === 'comments' && (row.comments || "")}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={columns.length + 1}
                            sx={{
                              position: "sticky",
                              left: 0,
                              textAlign: "left",
                              padding: "20px",
                              backgroundColor: "white",
                              zIndex: 1,
                            }}
                          >
                            No substages found.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
          </Box>
        )}
        </ThemeProvider>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={openDeleteDialog}
          onClose={(event, reason) => {
            if (reason === "backdropClick") return;
            setOpenDeleteDialog(false);
            setDeleteRow(null);
          }}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: "bold" }}>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography fontSize="0.9rem">
              Are you sure you want to delete Substage{" "}
              <strong>{deleteRow?.substage_desc}</strong>?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenDeleteDialog(false);
                setDeleteRow(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                try {
                  await apiService.delete(
                    `/substage/${deleteRow.substage_id}?cl_id=${deleteRow.cl_id}&env_id=${deleteRow.env_id}&app_id=${deleteRow.app_id}&job_id=${deleteRow.job_id}`,
                  );

                  setOpenDeleteDialog(false);
                  setDeleteRow(null);
                  fetchDbSubstages(deleteRow.job_id);
                  setSuccessMessage("Substage deleted successfully!");
                  setTimeout(() => setSuccessMessage(""), 3000);
                } catch (error) {
                  console.error("Delete failed:", error);
                  setOpenDeleteDialog(false);
                  setDeleteRow(null);

                  const errorMessage = handleDeleteError(error, "Substage");
                  setSuccessMessage(errorMessage);
                  setTimeout(() => setSuccessMessage(""), 5000);
                }
              }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default SubstageForm;
